import StrUtils from '@/utils/StrUtils'
import { Message } from '@/utils/Message'
import ColumnUtils from './ColumnUtils'
import request from '@/utils/request'
import URL from '@/api/urlTranslator'
import { getToken } from '@/utils/auth'
import convertResToBlob from '@/utils/convertResToBlob'
import i18n from '@/lang'
import { isNumber2 } from '@/utils/validate'
import { sortByMultiFieldAndOrder } from '@/utils/index'
import * as SystemConstants from '@/class/cargo/model/parameters/SystemConstants'
import * as ContainerLoadType from '@/class/cargo/model/parameters/ContainerLoadType'
import * as CargoSource from '@/class/cargo/model/parameters/CargoSource'
import * as FbaJobConstants from '@/class/cargo/model/parameters/FbaJobConstants'
import * as BookingTerm from '@/class/cargo/model/parameters/BookingTerm'
import * as ImportConstants from '@/class/cargo/model/parameters/ImportConstants'
import * as AirExportConstants from '@/class/cargo/model/parameters/AirExportConstants'
import * as BillingConstants from '@/class/cargo/model/parameters/BillingConstants'
import * as ChargeConstants from '@/class/cargo/model/parameters/ChargeConstants'

export async function exportExcel(fileName, rows, columns, convertNumber = true, totalConfig = null, sortMap = null, isString = false) {
  const { tHeader, textHeader, filterVal, exportRows } = dataConvert(rows, columns, convertNumber, sortMap, isString)
  const timestamp = new Date().getTime()
  console.log('====处理完数据时间======', timestamp)
  await import('@/vendor/Export2Excel').then(excel => {
    // await import('@/vendor/Export2Excel').then(excel => {
    const data = formatJson(filterVal, exportRows)
    excel.export_json_to_excel({
      header: tHeader,
      textHeader,
      data,
      filename: fileName,
      totalConfig
    })
  })
  console.log('====结束时间======', timestamp)
  Message.success(i18n.t('Message.RequestSucceededExporting'))
}
export async function exportExcelMoreSheetByGroup(fileName, rows, columns, fieldName, convertNumber = true, totalConfig = null) {
  const wb = await getExcelWbMoreSheetByGroup(fileName, rows, columns, fieldName)
  await exportExcelMoreSheetByWb(wb, fileName)
  Message.success(i18n.t('Message.RequestSucceededExporting'))
}
export async function getExcelWbMoreSheetByGroup(fileName, rows, columns, fieldName, convertNumber = true, totalConfig = null, ignoreFields = [], sortMap = null) {
  const { tHeader, textHeader, filterVal, exportRows } = dataConvert(rows, columns, convertNumber, sortMap)
  // 将exportRows根据fieldName分组
  const timestamp = new Date().getTime()
  console.log('====处理完数据时间======', timestamp)
  let wb = null
  await import('@/vendor/Export2Excel').then(excel => {
    // await import('@/vendor/Export2Excel').then(excel => {
    const data = formatJson(filterVal, exportRows)
    var dataArr = []
    var sheetnames = []
    const index = filterVal.findIndex(item => item === fieldName)
    // 导出忽略的field数组
    const positionsToRemove = ignoreFields.map(item => {
      const i = filterVal.indexOf(item)
      return i !== -1 ? i : null
    })
    // 对 positionsToRemove 按降序排序，以确保删除不会影响后续元素的索引
    positionsToRemove.sort((a, b) => b - a)
    const groupByGender = data.reduce((accumulator, currentValue) => {
      const key = currentValue[index]
      if (!accumulator.has(key)) {
        accumulator.set(key, [])
      }

      for (const position of positionsToRemove) {
        if (position >= 0 && position < currentValue.length) {
          currentValue.splice(position, 1) // 第二个参数 1 表示删除一个元素
        }
      }

      accumulator.get(key).push(currentValue)
      return accumulator
    }, new Map())
    // 分组排序
    const groupMap = new Map([...groupByGender.entries()].sort((a, b) => a[0].localeCompare(b[0])))
    // 处理各个sheet的数据
    const moreHeader = []
    for (const position of positionsToRemove) {
      if (position >= 0 && position < tHeader.length) {
        tHeader.splice(position, 1) // 第二个参数 1 表示删除一个元素
      }
    }
    groupMap.forEach((value, key) => {
      moreHeader.push(tHeader)
      dataArr.push(value)
      sheetnames.push(key)
    })
    wb = excel.get_excel_wb_with_more_sheet({
      header: moreHeader,
      moreHeader,
      data: dataArr,
      filename: fileName,
      sheetname: sheetnames,
      totalConfig
    })
  })
  console.log('====结束时间======', timestamp)
  return wb
}
export async function exportExcelMoreSheetByWb(wb, filename) {
  await import('@/vendor/Export2Excel').then(excel => {
    excel.export_wb_to_excel_with_more_sheet({
      wb, filename
    })
  })
}

export async function exportPdf(fileName, rows, columns, convertNumber = true) {
  const { tHeader, filterVal, exportRows } = dataConvert(rows, columns, convertNumber)
  const tableHeader = tHeader.map((item, index) => {
    return {
      field: filterVal[index], headerName: item
    }
  })
  const data = new FormData()
  data.append('tableHeader', JSON.stringify(tableHeader))
  let dataCache = []
  data.append('tableBody', JSON.stringify(exportRows, function(key, value) {
    if (typeof value === 'object' && value !== null) {
      if (dataCache.indexOf(value) !== -1) {
        return
      }
      dataCache.push(value)
    }
    return value
  }))
  dataCache = null
  data.append('fileName', JSON.stringify(fileName))
  try {
    const res = await request({
      url: URL.report.exportPdf + '?sessionId=' + getToken(),
      config: {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      },
      method: 'post',
      responseType: 'blob',
      data
    })
    convertResToBlob(res)
    Message.success(i18n.t('Message.RequestSucceededExporting'))
  } catch (err) {
    console.log(err)
  }
}

export function dataConvert(rows, columns, convertNumber, sortMap = null, isString = false) {
  const tHeader = []
  const textHeaderSet = new Set() // 文本列集合
  const filterVal = []
  // 重新定义 rows 避免导出修改了grid上的值
  const exportRows = []
  // 对行中每列的值做处理
  for (const item of rows) {
    if (!item) continue
    const exportItem = { ... item }
    for (const column of columns) {
      let colDef
      if (column.hasOwnProperty('columnType')) {
        // 处理新增的SOP列
        colDef = column
      } else {
        colDef = column.colDef
      }
      if (!colDef || colDef.noExport) continue

      const dataField = colDef.field
      if (!colDef.headerName || !dataField) continue
      if (!tHeader.includes(colDef.headerName)) {
        tHeader.push(colDef.headerName)
        filterVal.push(dataField)
      }

      let data = item.hasOwnProperty(dataField) ? item[dataField] : ''
      const originData = data

      // 收发通字段导出时保留完整数据，不执行valueFormatter
      const skipValueFormatterFields = [
        'shipperLine1', 'consigneeLine1', 'notifyLine1',
        'mblShipperLine1', 'mblConsigneeLine1', 'mblNotifyLine1',
        'hblShipperLine1', 'hblConsigneeLine1', 'hblNotifyLine1'
      ]
      const shouldSkipValueFormatter = skipValueFormatterFields.includes(dataField)

      // 进行labelFunction 处理
      if (colDef.excelRenderer && typeof colDef.excelRenderer !== 'string') { // excel导出指定转换函数
        const params = { data: item, value: item[dataField], colDef }
        data = colDef.excelRenderer(params)
      } else if (colDef.cellRenderer && typeof colDef.cellRenderer !== 'string') {
        const params = { data: item, value: item[dataField], colDef }
        data = colDef.cellRenderer(params)
      } else if (!shouldSkipValueFormatter && colDef.valueFormatter && typeof colDef.valueFormatter !== 'string') {
        const params = { data: item, value: item[dataField] }
        data = colDef.valueFormatter(params)
      } else if (colDef.valueGetter && typeof colDef.valueGetter !== 'string') {
        const params = { data: item, value: item[dataField] }
        data = colDef.valueGetter(params)
      }

      // cellRendererFramework 的 cargo-cell-select/ cargo-advance-select 处理
      if (colDef.cellRendererFramework && colDef.custom) {
        if (colDef.custom.tag === 'cargo-cell-select') {
          const options = colDef.custom.attrs.cellSelectAttrs.fullOptions
          if (options) {
            const selected = options.find(p => p[colDef.custom.optionField.value] === item[dataField])
            if (selected) {
              data = selected[colDef.custom.optionField.label]
            }
          }
        } else if (colDef.custom.tag === 'cargo-advance-select') {
          data = ColumnUtils.resolveColumnLabel(colDef.custom.selectType, colDef.custom.tagValue, item[dataField], colDef.custom.tagName)
        } else if (colDef.custom.tag === 'span') {
          if (colDef.headerName === '是否确认' || colDef.headerName === 'Confirm State') { // 对账新版特殊处理， 确认状态和是否确认显示列对应同一个字段
            data = ColumnUtils.chargeConfirmStateLabFun(data)
          }
        }
      }

      /* if (data && !StrUtils.isChinese(data) && !StrUtils.isEnglish(data) && convertNumber) {
        // 去掉千位分隔符
        let dataStr = data.toString()
        dataStr = dataStr.replace(/,/g, '')
        const precision = dataStr.split('.')[1] ? dataStr.split('.')[1].length : 0
        if (!isNaN(Number(dataStr)) && precision) {
          data = Number(dataStr).toFixed(precision) // 小数位，与列表显示一致
        }
      }*/
      if (data && typeof data === 'string') {
        data = StrUtils.replaceSpecialCharacter(data)
        if (!colDef.hasOwnProperty('convertToNumber') && data.length >= 12 && typeof originData === 'string' && originData && isNumber2(data) && !isString) {
          textHeaderSet.add(colDef.headerName)
        }
      }
      // item[dataField] = data
      exportItem[dataField] = data
    }
    exportRows.push(exportItem)
  }
  console.log('tHeader', tHeader)
  console.log('filterVal', filterVal)
  const textHeader = []
  if (textHeaderSet.size > 0) {
    for (let i = 0; i < tHeader.length; ++i) {
      if (textHeaderSet.has(tHeader[i])) {
        textHeader.push(i)
      }
    }
  }
  const resultExportRows = sortMap ? sortByMultiFieldAndOrder(exportRows, sortMap) : exportRows
  return { tHeader, textHeader, filterVal, exportRows: resultExportRows }
}

function formatJson(filterVal, jsonData) {
  return jsonData.map(v => filterVal.map(j => v[j]))
}

export async function exportExcelMultiHeader(multiHeader, merges, fileName, rows, columns, convertNumber = true, totalConfig = null) {
  const { tHeader, filterVal, exportRows } = dataConvert(rows, columns, convertNumber)
  const timestamp = new Date().getTime()
  console.log('====处理完数据时间======', timestamp)
  await import('@/vendor/Export2Excel').then(excel => {
    // await import('@/vendor/Export2Excel').then(excel => {
    const data = formatJson(filterVal, exportRows)
    excel.export_json_to_excel({
      multiHeader: multiHeader,
      merges: merges,
      header: tHeader,
      data,
      filename: fileName,
      totalConfig
    })
  })
  console.log('====结束时间======', timestamp)
  Message.success(i18n.t('Message.RequestSucceededExporting'))
}

export function copySheet(sourceFile, sourceSheetName, targetWorkBook, targetSheetName) {
  import('@/vendor/Export2Excel').then(excel => {
    excel.copySheet({
      sourceFile,
      sourceSheetName,
      targetWorkBook,
      targetSheetName
    })
  })
}

export function columnsConvert(columns) {
  const columnDefinition = {}
  for (const column of columns) {
    let colDef
    if (column.hasOwnProperty('columnType')) {
      // 处理新增的SOP列
      colDef = column
    } else {
      colDef = column.colDef
    }
    if (!colDef || colDef.noExport) {
      continue
    }
    const dataField = colDef.field
    if (!colDef.headerName || !dataField) {
      continue
    }
    columnDefinition[dataField] = colDef.headerName
  }
  return columnDefinition
}

export function valueFormatterConstants() {
  const constants = {}
  constants['lob'] = [...SystemConstants.LOBS]
  constants['containerLoadType'] = [
    ...ContainerLoadType.CONTAINERLOAD_TYPES,
    ...ContainerLoadType.IE_TYPES
  ]
  constants['cargoSource'] = [...CargoSource.CARGO_SOURCES]
  constants['destinationDistribution'] = [...FbaJobConstants.DESTINATION_DISTRIBUTION_TYPES]
  constants['bookingTerm'] = [...BookingTerm.BOOKING_TERMS_ALL]
  constants['operateTypeAI'] = [...ImportConstants._cargoTypeAirInputList]
  constants['operateTypeAE'] = [...AirExportConstants.BUSINESS_MODELS]
  constants['receivableInvoiceState'] = [...BillingConstants.BILLING_INVOICE_STATES]
  constants['payableInvoiceState'] = [...BillingConstants.BILLING_INVOICE_STATES]
  constants['pushExternalInterfaceStatus'] = [...ChargeConstants.ZY_INTERFACE_PUSH_STATUS]
  constants['saleProductCode'] = [...ChargeConstants.ZY_INTERFACE_PUSH_STATUS]
  constants['fullOrInside'] = [...SystemConstants.FULL_OR_INSIDE]
  return constants
}
